import { getCycleNodesInGraph, getCyclesInGraph, makeGraphFromEdges } from "@glideapps/graphs";
import { DefaultMap, definedMap, panic } from "@glideapps/ts-necessities";
import { areEqual, hasOwnProperty } from "collection-utils";
import shuffle from "lodash/shuffle";
import sortBy from "lodash/sortBy";
import { isKeyRefObject } from "../common/components/data";
import {
    convertFieldNamesToColumnNamesWithMapping,
    makeColumnMappingForJSONFromDocumentData,
} from "../common/components/datastore/field-names";
import type {
    AppUserAuthenticator,
    ComputationModel,
    MinimalAppEnvironment,
    PathForColumn,
    PathWithThunksFlag,
    RecomputeResult,
    RecomputeTiming,
    TemporaryComputedColumn,
    UserProfileTableInfo,
} from "../common/components/types";
import type { BasePrimitiveValue, GroundValue, LoadedGroundValue, Row, Table } from "../common/computation-model/data";
import {
    asString,
    asTable,
    follow,
    getRowColumn,
    isLoadingValue,
    isPrimitiveValue,
    tableForEach,
} from "../common/computation-model/data";
import type { NamespaceGraph, NamespaceGraphNode } from "../common/computation-model/namespace-graph";
import type { KeyPath, Path, RelativePath, RootPath, ValueAndFormatPaths } from "../common/computation-model/paths";
import {
    amendPath,
    getSymbolicRepresentationForPath,
    isRootPath,
    isTopLevelPath,
    makeKeyPath,
    makePath,
    makeRootPath,
} from "../common/computation-model/paths";
import type { TableKeeper } from "../common/computation-model/table-keeper";
import type { TableKeeperStore } from "../common/computation-model/table-keeper-store";
import type {
    AsyncComputation,
    Computation,
    Handler,
    IncomingSlot,
    Namespace,
    RootPathResolver,
    TableAggregateComputation,
} from "../common/computation-model/types";
import type { Database, Query, RowIndex } from "../common/Database";
import type { TableName, UniversalTableName } from "../common/database-strings";
import {
    appUserIDFieldName,
    areTableNamesEqual,
    makePrivateRowsPath,
    makePublicRowsPath,
    makeTablesPath,
    makeUserRowsPath,
    ownerAppUserIDsFieldName,
} from "../common/database-strings";
import { logError, logInfo } from "../common/debug-print";
import type {
    ColumnType,
    Formula,
    PrimitiveGlideTypeKind,
    SourceColumn,
    TableAndColumn,
    TableColumn,
    TableGlideType,
} from "../common/description";
import {
    FormulaKind,
    getSourceColumnPath,
    getTableColumn,
    getTableColumnDisplayName,
    getTableName,
    getTableRefTableName,
    isComputedColumn,
    isMultiRelationType,
    isPrimitiveType,
    isRelationType,
    isSingleRelationType,
    makeSourceColumn,
    sheetNameForTable,
    SourceColumnKind,
    SpecialValueKind,
    tableHasRowOwners,
} from "../common/description";
import type { WithUserEnteredTextFormula } from "../common/formula";
import { GeneratedImageKind, isUniversalTableName } from "../common/formula";
import { rowIndexColumnName } from "../common/google-sheets";
import { makeRowID } from "../common/make-row-id";
import { convertGlideDateTime } from "../date-time/glide-date-time";
import type { SchemaInspector } from "../generator/components";
import {
    getEffectiveDisplayFormulaForColumn,
    hasMathFormulaRandom,
    makeQuotaKeyForFormula,
} from "../generator/formulas/compiler";
import type {
    ArraySourceSpecification,
    ColumnOrValueSpecification,
    QueryParametersSpecification,
    SingleValuePosition,
} from "../generator/formulas/formula-specifications";
import {
    ColumnOrValueKind,
    decomposeAll,
    decomposeFormatFormula,
    RollupKind,
    SingleValuePositionKind,
    SyntheticColumnKind,
    ValueFormatKind,
} from "../generator/formulas/formula-specifications";
import { parseMath } from "../generator/math";
import type { JSONObject, TextTemplateToken } from "../support";
import {
    assert,
    assertNever,
    checkString,
    defined,
    filterUndefined,
    isArray,
    mapFilter,
    mapFilterUndefined,
    normalizeEmailAddress,
    tokenizeTemplateString,
    withStopwatch,
} from "../support";
import { ArrayMap, DefaultArrayMap } from "../support/array-map";
import {
    AllTrueComputation,
    AverageComputation,
    CountNonEmptyComputation,
    CountNotTrueComputation,
    CountTrueComputation,
    CountUniqueComputation,
    EarliestComputation,
    ExtractNthItemComputation,
    ExtractNthRowComputation,
    ExtractRandomItemComputation,
    ExtractRandomRowComputation,
    ExtractRowByMaximumComputation,
    ExtractRowByMinimumComputation,
    FirstOrLast,
    JoinStringsComputation,
    LatestComputation,
    MaximumComputation,
    MinimumComputation,
    MultiPrimitiveLookupComputation,
    MultiRelationLookupComputation,
    RangeComputation,
    SingleRelationComputation,
    SomeTrueComputation,
    SumComputation,
} from "./aggregates";
import { FormatDateTimeComputation, GeoDistanceAsyncComputation, YesCodeComputation } from "./async-computations";
import type { IfThenElseClause } from "./computations";
import {
    ConstructURLComputation,
    DynamicTextTemplateComputation,
    FormatDurationComputation,
    FormatNumberComputation,
    ForwardingKind,
    GenerateImageComputation,
    GlobalGuardedForwardComputation,
    IfThenElseComputation,
    MakeArrayComputation,
    MathComputation,
    ModifyDateComputation,
    ModifyDateKind,
    SingleLookupComputation,
    SplitStringComputation,
    StaticTextTemplateComputation,
} from "./computations";
import type { Condition, ConditionValuePath, PathOrGroundValue } from "./conditions";
import type { FilterPredicate, UserSpecificRow } from "./handlers";
import {
    AsyncComputationComputedColumnHandler,
    AsyncComputationHandler,
    CombineHandler,
    ComputationComputedColumnHandler,
    ComputationHandler,
    ConstantValueHandler,
    CurrentLocationHandler,
    FilterHandler,
    JoinUserSpecificRowsHandler,
    MultiRelationToColumnComputedColumnHandler,
    MultiRelationToColumnHandler,
    MultiRelationToGlobalComputedColumnHandler,
    MultiRelationToGlobalHandler,
    SingleRelationToColumnComputedColumnHandler,
    SingleRelationToGlobalComputedColumnHandler,
    SortByHandler,
    TableAggregateComputedColumnHandler,
    TableAggregateHandler,
    TimestampHandler,
    UUIDColumnHandler,
} from "./handlers";
import { NamespaceImpl } from "./namespace";
import { prepareCondition } from "./prepare-condition";
import { makeValueAndFormatPaths } from "./support";

const printTables = false;

export function prettyPrintTableAndColumn(tac: TableAndColumn): string {
    return `${sheetNameForTable(tac.table)}->${getTableColumnDisplayName(tac.column)}`;
}

export function getRowIDColumnName(table: TableGlideType): string {
    return table.rowIDColumn ?? rowIndexColumnName;
}

export function getRowIDForRowDocument(table: TableGlideType, row: JSONObject): string {
    // Sometimes a table has a row ID column set, but its rows don't have that
    // column, so we fall back to the row index.
    return asString((row[getRowIDColumnName(table)] ?? row[rowIndexColumnName]) as LoadedGroundValue);
}

function getColumnsToLoad(
    table: TableGlideType,
    withRowIndex: boolean,
    withRowID: boolean
): [readonly TableColumn[], readonly TableColumn[]] {
    const userAgnosticColumns: TableColumn[] = [];
    const userSpecificColumns: TableColumn[] = [];
    for (const c of table.columns) {
        if (!isPrimitiveType(c.type) && !isOldStyleRelation(c)) continue;
        if (!withRowID && c.name === table.rowIDColumn) continue;
        if (c.hidden === true && (c.name !== rowIndexColumnName || !withRowIndex)) continue;
        if (c.isProtected === true) continue;
        if (isComputedColumn(c)) continue;
        if (c.isUserSpecific === true) {
            userSpecificColumns.push(c);
        } else {
            userAgnosticColumns.push(c);
        }
    }
    return [userAgnosticColumns, userSpecificColumns];
}

function addColumnsToRow(row: Row, converted: JSONObject, columnNames: readonly string[]): void {
    for (const name of columnNames) {
        const v = convertGlideDateTime(converted[name]);
        if (v === undefined) continue;
        if (!isPrimitiveValue(v) && !isKeyRefObject(v)) continue;
        // FIXME: We should do something about this type - they don't allow
        // key ref objects.
        row[name] = v as any;
    }
}

async function loadRowsForGlideTable(
    db: Database,
    appID: string,
    // `false` means there's no app user, and we want to load all rows.  This
    // will only work on the backend.
    appUserID: string | undefined | false,
    table: TableGlideType,
    withRowIndex: boolean,
    withRowID: boolean
): Promise<[Row[], UserSpecificRow[]]> {
    const tableName = getTableName(table);
    const tablesPath = makeTablesPath(appID);

    const [columns, userColumns] = getColumnsToLoad(table, withRowIndex, withRowID);

    let rowsPath: string;
    let queries: readonly Query[];
    if (!tableHasRowOwners(table)) {
        rowsPath = makePublicRowsPath(tablesPath, tableName);
        queries = [];
    } else {
        rowsPath = makePrivateRowsPath(tablesPath, tableName);
        if (appUserID === false) {
            queries = [];
        } else {
            assert(appUserID !== undefined, "Must have an app user for row-owned tables");
            queries = [
                {
                    fieldPath: ownerAppUserIDsFieldName,
                    opString: "array-contains",
                    value: appUserID,
                },
            ];
        }
    }
    const docs = sortBy(
        (await db.getDocumentsWhere(rowsPath, queries)).map(x => x.data),
        d => d[rowIndexColumnName]
    );

    const rows: Row[] = [];
    const mapping = makeColumnMappingForJSONFromDocumentData(new Map(), table);
    for (const data of docs) {
        const converted = convertFieldNamesToColumnNamesWithMapping(db.convertFromDocument(data), mapping);
        const rowID = getRowIDForRowDocument(table, converted);
        const row: Row = { $rowID: rowID, $isVisible: true };
        if (withRowIndex) {
            row[rowIndexColumnName] = data[rowIndexColumnName];
        }
        addColumnsToRow(
            row,
            converted,
            columns.map(c => c.name)
        );
        rows.push(row);
    }

    let userRows: UserSpecificRow[];

    if (appUserID !== undefined && userColumns.length > 0) {
        const userRowsPath = makeUserRowsPath(tablesPath, tableName);
        const userDocs = await db.getDocumentsWhere(userRowsPath, [
            {
                fieldPath: appUserIDFieldName,
                opString: "==",
                value: appUserID,
            },
        ]);
        userRows = userDocs.map(({ data, id }) => {
            const converted = convertFieldNamesToColumnNamesWithMapping(db.convertFromDocument(data.data), mapping);
            const r: UserSpecificRow = {
                $rowID: id,
                $isVisible: true,
                $keyColumnName: checkString(data.keyColumnName),
                $keyColumnValue: defined(data.keyColumnValue),
            };
            addColumnsToRow(
                r,
                converted,
                userColumns.map(c => c.name)
            );
            return r;
        });
    } else {
        userRows = [];
    }

    return [rows, userRows];
}

export async function loadRowsForTablesInApp(
    db: Database,
    appID: string,
    appUserID: string | undefined | false,
    inspector: SchemaInspector
): Promise<Map<TableGlideType, [Row[], UserSpecificRow[]]>> {
    const rowsForTable = new Map<TableGlideType, [Row[], UserSpecificRow[]]>();
    for (const table of inspector.schema.tables) {
        const rows = await loadRowsForGlideTable(db, appID, appUserID, table, true, true);
        rowsForTable.set(table, rows);
    }
    return rowsForTable;
}

// Returns the item column names if its a primitive array type.
function isSheetArrayType(t: ColumnType): readonly string[] | undefined {
    if (t.kind === "array" && t.itemColumnNames !== undefined) {
        assert(isPrimitiveType(t.items));
        return t.itemColumnNames;
    }
    return undefined;
}

function isOldStyleRelation(
    c: TableColumn
): { tableName: TableName; columnName: string; isMultiple: boolean } | undefined {
    // We have at least one app that has a column that's apparently both
    // computed but is also an old-style relation.  I don't know how that can
    // happen, but in order to at least not crash we just treat it as a
    // computed column, vs an old-style relation.  In this particular case at
    // least there is no old-style relation data in the table data.
    //
    // https://glide-help.zendesk.com/agent/tickets/6397
    if (isComputedColumn(c)) return undefined;
    if (isSingleRelationType(c.type)) {
        if (c.type.refColumnName !== undefined) {
            return { tableName: getTableRefTableName(c.type), columnName: c.type.refColumnName, isMultiple: false };
        }
    } else if (isMultiRelationType(c.type)) {
        if (c.type.items.refColumnName !== undefined) {
            return {
                tableName: getTableRefTableName(c.type.items),
                columnName: c.type.items.refColumnName,
                isMultiple: true,
            };
        }
    }
    return undefined;
}

class AppUserEmailAddressHandler implements Handler {
    private _email: string | undefined | false;
    private _ns: Namespace | undefined;
    public readonly symbolicRepresentation = "(app-user-email-address)";

    constructor(private readonly _authenticator: AppUserAuthenticator | undefined, private readonly _real: boolean) {}

    private readonly appUserChanged = (): void => {
        this._email = undefined;

        this._ns?.pushDirt(this, { kind: "table", columns: true });
    };

    public getSlots(): readonly IncomingSlot[] {
        return [];
    }

    public get isDirty(): boolean {
        return false;
    }

    public recompute(): GroundValue {
        if (this._email === undefined) {
            if (this._real) {
                this._email = this._authenticator?.realEmail ?? false;
            } else {
                this._email = this._authenticator?.virtualEmail ?? false;
            }
        }
        if (this._email === false) return undefined;
        return this._email;
    }

    public setDirty(): void {
        return;
    }

    public connect(ns: Namespace): void {
        assert(this._ns === undefined);
        this._ns = ns;
        this._authenticator?.addCallback(this.appUserChanged);
    }

    public disconnect(ns: Namespace): void {
        assert(this._ns === ns);
        this._ns = undefined;
        this._authenticator?.removeCallback(this.appUserChanged);
    }
}

class UserProfileFilterPredicate implements FilterPredicate {
    constructor(private readonly _virtualEmailPath: RootPath, private readonly _emailColumnPath: RelativePath) {}

    public getFilteredPaths(): readonly Path[] {
        return [this._virtualEmailPath, this._emailColumnPath];
    }

    public getHostPaths(): readonly RelativePath[] {
        return [];
    }

    public isRowIncluded(ns: RootPathResolver, row: Row): boolean {
        const emailValue = ns.get(this._virtualEmailPath, true);
        if (isLoadingValue(emailValue)) return false;
        const email = definedMap(emailValue, asString);
        if (email === undefined) return false;

        const v = follow(row, this._emailColumnPath);
        if (typeof v !== "string") return false;
        return normalizeEmailAddress(v) === email;
    }

    public get symbolicRepresentation(): string {
        return `(user-profile-filter virtual: ${getSymbolicRepresentationForPath(
            this._virtualEmailPath
        )} real: ${getSymbolicRepresentationForPath(this._emailColumnPath)})`;
    }
}

type TableAndColumnInfo = TableAndColumn & PathForColumn;

function makeTableAndColumnInfo(
    table: TableGlideType,
    column: TableColumn,
    pathForColumn: PathForColumn
): TableAndColumnInfo {
    return { table, column, ...pathForColumn };
}

function isRelationKeyArray(column: TableColumn): boolean {
    return column.type.kind === "array" || isOldStyleRelation(column) !== undefined;
}

enum BuildResult {
    WaitingOnDependency,
    Faulty,
    NotImplemented,
}

function isBuildResult(x: unknown): x is BuildResult {
    return typeof x === "number";
}

function missing(tac: TableAndColumn, message: string): BuildResult {
    logError("Missing in", prettyPrintTableAndColumn(tac), ":", message);
    return BuildResult.NotImplemented;
}

const rowIndexPath = makeKeyPath(rowIndexColumnName);

interface ColumnBuildResult {
    readonly allColumnsBuilt: boolean;
    readonly dependencyCycles: boolean;
}

interface Metadata {
    readonly tableAndColumn: TableAndColumn | undefined;
}

interface CurrentTableAndColumn {
    readonly tac: TableAndColumn;
    readonly columnDependencies: Set<TableAndColumn>;
    readonly entitiesAdded: RootPath[];
}

export class ComputationModelImpl implements ComputationModel {
    public readonly ns: NamespaceImpl<Metadata>;

    // This being `undefined` means that computed columns have not being built
    // yet.
    private _columnBuildResult: ColumnBuildResult | undefined;

    // The path of the table keeper for a table.
    private readonly _tableBasePaths = new ArrayMap<TableName, RootPath>(areTableNamesEqual);
    // The path of the node that also has the global columns.  If a table
    // doesn't have global columns, this will be the same as the one in
    // `_tableBasePaths`.
    private readonly _tableFullPaths = new ArrayMap<TableName, RootPath>(areTableNamesEqual);
    // Path of the node that produces a given column.
    private readonly _columnPaths = new Map<TableAndColumn, PathForColumn | BuildResult>();
    // Path of the node that produces the formatted version of a given column
    private readonly _formatPaths = new Map<TableAndColumn, PathForColumn>();
    // Path of the first row of a given table.
    private readonly _firstTableRows = new ArrayMap<TableName, RootPath>(areTableNamesEqual);
    // Path of the last row of a given table.
    private readonly _lastTableRows = new ArrayMap<TableName, RootPath>(areTableNamesEqual);
    // Path of the user profile row.
    private _userProfileRowPath: RootPath | undefined;
    // Path of the "current time" value.
    private _timestampPath: RootPath | undefined;
    private _startOfTodayPath: RootPath | undefined;
    private _endOfTodayPath: RootPath | undefined;
    // Path of the constant `undefined`.
    private _undefinedPath: RootPath | undefined;
    private _emptyArrayPath: RootPath | undefined;
    private _verifiedEmailAddressPath: RootPath | undefined;
    private _realEmailAddressPath: RootPath | undefined;
    private _currentLocationPath: RootPath | undefined;
    // Path of the node that produces a specific column in the user profile
    // row.  This will usually be a combination node, and have the full
    // dirtiness for that value.
    private readonly _userProfilePaths = new Map<string, TableAndColumn & PathWithThunksFlag>();
    private readonly _userProfileFormattedPaths = new Map<string, TableAndColumn & PathWithThunksFlag>();
    // Paths for constants.  NOTE: This won't work for `GlideDateTime`s, but
    // we shouldn't have those as constants.
    private readonly _constantPaths = new Map<BasePrimitiveValue, RootPath>();
    // This is a map of combination nodes, so we don't generate
    // duplicates.  The map key is the key of the node that provides
    // the value/result for the combination node.
    // key -> [key[], path][]
    private readonly _combinedPaths = new DefaultMap<string, [ReadonlySet<string>, RootPath][]>(() => []);
    // Both `TableGlideType`s as well as `TableColumn`s are unique, so we
    // can use them as keys here.  This map make `TableAndColumn`s unique.
    // table -> column -> tac
    private readonly _tableAndColumns = new DefaultArrayMap<TableName, Map<TableColumn, TableAndColumn>>(
        areTableNamesEqual,
        () => new Map()
    );
    private readonly _fallbackUserProfileRowID = makeRowID();
    private readonly _dependenciesForColumn: Map<TableAndColumn, Set<TableAndColumn>> = new Map();
    // Must be invalidated whenever `_columnPaths` or `_formatPaths` or any
    // table base paths change.  In practice this shouldn't happen after the
    // model has been built.
    private readonly _cachedColumnPaths = new ArrayMap<TableName, ReadonlyMap<string, ValueAndFormatPaths>>(
        areTableNamesEqual
    );

    // The table and column currently being built.
    private _currentTableAndColumn: CurrentTableAndColumn | undefined;

    // This is only used for timing
    private _numRowsAfterAdding = 0;

    constructor(
        private readonly _inspector: SchemaInspector,
        public readonly tableKeeperStore: TableKeeperStore<TableKeeper>,
        // The app environment is not strictly necessary.  Some things won't
        // work, such as geo distance, yes-code, and user profiles.
        private readonly _appEnvironment: MinimalAppEnvironment | undefined,
        debugPrint: boolean
    ) {
        this.ns = new NamespaceImpl(_appEnvironment?.appID, debugPrint);

        for (const table of this.getTables()) {
            const tableName = getTableName(table);
            const displayName = sheetNameForTable(table);
            const keeper = tableKeeperStore.getTableKeeperForTable(tableName);
            const keeperPath = this.addEntity(displayName, keeper, false);
            const sortByPath = this.addEntity(
                `sort(${displayName})`,
                new SortByHandler(amendPath(keeperPath, { c: rowIndexColumnName })),
                false
            );

            const userKeeper = tableKeeperStore.getUserSpecificTableKeeperForTable(tableName);
            if (userKeeper !== undefined) {
                // FIXME: Sort after joining!

                const userPath = this.addEntity("user " + displayName, userKeeper, false);
                const [, userColumns] = getColumnsToLoad(table, false, false);
                const joinHandler = new JoinUserSpecificRowsHandler(
                    sortByPath,
                    userPath,
                    userColumns.map(c => c.name)
                );
                const joinPath = this.addEntity("join " + displayName, joinHandler, false);
                const combineHandler = new CombineHandler(joinPath, [
                    {
                        kind: "pass-through",
                        path: joinPath,
                    },
                    {
                        kind: "pass-through",
                        path: sortByPath,
                    },
                ]);
                const combinePath = this.addEntity("combine with join" + displayName, combineHandler, false);
                this._tableBasePaths.set(tableName, combinePath);
            } else {
                this._tableBasePaths.set(tableName, sortByPath);
            }
        }

        this.updateFallbackUserProfileRow();
        this._appEnvironment?.authenticator.addCallback(this.updateFallbackUserProfileRow);
    }

    public retire(): void {
        const info = this.getUserProfileKeeper();
        if (info !== undefined) {
            info[1].deleteInvisibleRow(this._fallbackUserProfileRowID);
        }

        this.ns.retire();
        this._appEnvironment?.authenticator.removeCallback(this.updateFallbackUserProfileRow);
    }

    // `f` must return whether building the column was successful.  If it
    // wasn't, this will remove all entities that have been added to the
    // namespace for this column.
    private withCurrentTableAndColumn(tac: TableAndColumn, f: () => boolean): void {
        assert(this._currentTableAndColumn === undefined);
        this._currentTableAndColumn = {
            tac,
            columnDependencies: new Set(),
            entitiesAdded: [],
        };

        let success = false;
        try {
            success = f();
        } finally {
            if (!success) {
                for (;;) {
                    // We have to go in reverse order to respect dependencies.
                    const path = this._currentTableAndColumn.entitiesAdded.pop();
                    if (path === undefined) break;
                    this.ns.deleteEntity(path);
                }
            }

            this._dependenciesForColumn.set(tac, this._currentTableAndColumn.columnDependencies);
            this._currentTableAndColumn = undefined;
        }
    }

    public makeGraph(): NamespaceGraph {
        this.buildComputedColumns();

        return this.ns.makeGraph();
    }

    public debugPrintNode(node: NamespaceGraphNode): void {
        this.ns.debugPrintNode(node);
    }

    private getUserProfileKeeper(): [UserProfileTableInfo, TableKeeper] | undefined {
        const { userProfileTableInfo } = this._inspector;
        if (userProfileTableInfo === undefined) return undefined;

        const keeper = this.tableKeeperStore.getTableKeeperForTable(userProfileTableInfo.tableName);

        return [userProfileTableInfo, keeper];
    }

    private readonly updateFallbackUserProfileRow = (): void => {
        const info = this.getUserProfileKeeper();
        if (info === undefined) return;
        const [userProfileTableInfo, keeper] = info;

        const email = this._appEnvironment?.authenticator.virtualEmail;
        keeper.deleteInvisibleRow(this._fallbackUserProfileRowID);
        if (email !== undefined) {
            keeper.addInvisibleRow({
                $rowID: this._fallbackUserProfileRowID,
                $isVisible: false,
                [userProfileTableInfo.emailColumnName]: email,
            });
        }
    };

    private getTables(): readonly TableGlideType[] {
        return this._inspector.schema.tables.filter(t => !getTableName(t).isSpecial);
    }

    public getTimestampPath(): RootPath {
        if (this._timestampPath === undefined) {
            this._timestampPath = this.addEntity("timestamp", new TimestampHandler(), false);
        }
        return this._timestampPath;
    }

    public getStartOrEndOfTodayPath(startOrEnd: "start" | "end"): RootPath {
        if (startOrEnd === "start") {
            if (this._startOfTodayPath === undefined) {
                const now = this.getTimestampPath();
                this._startOfTodayPath = this.addEntity(
                    "startOfToday",
                    new ComputationHandler(new ModifyDateComputation(now, ModifyDateKind.StartOfDay)),
                    false
                );
            }
            return this._startOfTodayPath;
        } else if (startOrEnd === "end") {
            if (this._endOfTodayPath === undefined) {
                const now = this.getTimestampPath();
                this._endOfTodayPath = this.addEntity(
                    "endOfToday",
                    new ComputationHandler(new ModifyDateComputation(now, ModifyDateKind.EndOfDay)),
                    false
                );
            }
            return this._endOfTodayPath;
        } else {
            return assertNever(startOrEnd);
        }
    }

    private getUndefinedPath(): RootPath {
        if (this._undefinedPath === undefined) {
            this._undefinedPath = this.addEntity("undefined", new ConstantValueHandler(undefined), false);
        }
        return this._undefinedPath;
    }

    private getEmptyArrayPath(): RootPath {
        if (this._emptyArrayPath === undefined) {
            this._emptyArrayPath = this.addEntity("empty array", new ConstantValueHandler([]), false);
        }
        return this._emptyArrayPath;
    }

    public getVerifiedEmailAddressPath(): RootPath {
        if (this._verifiedEmailAddressPath === undefined) {
            this._verifiedEmailAddressPath = this.addEntity(
                "verifiedEmailAddress",
                new AppUserEmailAddressHandler(this._appEnvironment?.authenticator, false),
                false
            );
        }
        return this._verifiedEmailAddressPath;
    }

    public getRealEmailAddressPath(): RootPath {
        if (this._realEmailAddressPath === undefined) {
            this._realEmailAddressPath = this.addEntity(
                "realEmailAddress",
                new AppUserEmailAddressHandler(this._appEnvironment?.authenticator, true),
                false
            );
        }
        return this._realEmailAddressPath;
    }

    private getCurrentLocationPath(): RootPath {
        if (this._currentLocationPath === undefined) {
            this._currentLocationPath = this.addEntity("currentLocation", new CurrentLocationHandler(), false);
        }
        return this._currentLocationPath;
    }

    private addEntity(name: string, handler: Handler, forCurrentColumnOnly: boolean): RootPath {
        let metadata: Metadata | undefined;
        if (forCurrentColumnOnly) {
            assert(this._currentTableAndColumn !== undefined);
            metadata = { tableAndColumn: this._currentTableAndColumn.tac };
        }
        const path = this.ns.addEntity(name, handler, metadata);
        if (forCurrentColumnOnly) {
            defined(this._currentTableAndColumn).entitiesAdded.push(path);
        }
        return path;
    }

    private addExtractFirstRowEntity(entityName: string, tablePath: RootPath, includeInvisible: boolean): RootPath {
        return this.addEntity(
            entityName,
            new TableAggregateHandler(
                tablePath,
                new ExtractRowByMinimumComputation(makePath(rowIndexColumnName), undefined, includeInvisible)
            ),
            false
        );
    }

    private getFirstRowOfTable(table: TableGlideType): RootPath {
        const tableName = getTableName(table);
        let firstRow = this._firstTableRows.get(tableName);
        if (firstRow === undefined) {
            firstRow = this.addExtractFirstRowEntity(
                `first(${sheetNameForTable(table)})`,
                defined(this._tableBasePaths.get(tableName)),
                false
            );
            this._firstTableRows.set(tableName, firstRow);
        }
        return firstRow;
    }

    private getPathsForSingleValueOffset(
        contextTable: TableGlideType | undefined,
        offset: ColumnOrValueSpecification<number>
    ): { path: Path; tablePath: RootPath | undefined; usesThunks: boolean } | BuildResult {
        // If we need an offset and it's in the context table, we have to return its table path.
        if (offset.kind === ColumnOrValueKind.Column) {
            const positionInfo = this.makePathForSourceColumn(defined(contextTable), offset.column, false);
            if (isBuildResult(positionInfo)) return positionInfo;

            return { path: positionInfo.path, tablePath: positionInfo.tablePath, usesThunks: positionInfo.usesThunks };
        } else if (offset.kind === ColumnOrValueKind.Constant) {
            return { path: this.makePathForConstant(offset.value), tablePath: undefined, usesThunks: false };
        } else if (offset.kind === ColumnOrValueKind.Empty || offset.kind === ColumnOrValueKind.SpecialValue) {
            return BuildResult.Faulty;
        } else {
            return assertNever(offset);
        }
    }

    private getAggregateComputationForSingleValuePosition(
        position: SingleValuePosition,
        valuePath: RelativePath | undefined,
        contextTable: TableGlideType | undefined
    ):
        | {
              computation: TableAggregateComputation<unknown, any>;
              tablePath: RootPath | undefined;
              usesThunks: boolean;
              canBeGlobal: boolean;
          }
        | BuildResult {
        // If we need the row index, we have to return its table path in the aggregated table.

        switch (position.kind) {
            case SingleValuePositionKind.First:
                return {
                    computation: new ExtractRowByMinimumComputation(makePath(rowIndexColumnName), valuePath, false),
                    tablePath: undefined,
                    canBeGlobal: true,
                    usesThunks: false,
                };
            case SingleValuePositionKind.Last:
                return {
                    computation: new ExtractRowByMaximumComputation(makePath(rowIndexColumnName), valuePath),
                    tablePath: undefined,
                    canBeGlobal: true,
                    usesThunks: false,
                };
            case SingleValuePositionKind.FromStart:
            case SingleValuePositionKind.FromEnd:
                const positionPaths = this.getPathsForSingleValueOffset(contextTable, position.offset);
                if (isBuildResult(positionPaths)) return positionPaths;

                return {
                    computation: new ExtractNthRowComputation(
                        makePath(rowIndexColumnName),
                        valuePath,
                        position.kind === SingleValuePositionKind.FromStart ? FirstOrLast.First : FirstOrLast.Last,
                        positionPaths.path
                    ),
                    tablePath: positionPaths.tablePath,
                    canBeGlobal: true,
                    usesThunks: positionPaths.usesThunks,
                };
            case SingleValuePositionKind.Random:
                return {
                    computation: new ExtractRandomRowComputation(valuePath),
                    tablePath: undefined,
                    canBeGlobal: false,
                    usesThunks: false,
                };
            default:
                return assertNever(position);
        }
    }

    private extractRow(
        table: TableGlideType,
        aggregateComputation: TableAggregateComputation<unknown>,
        forCurrentColumnOnly: boolean
    ): RootPath {
        return this.addEntity(
            `${aggregateComputation.displayName}(${sheetNameForTable(table)})`,
            new TableAggregateHandler(defined(this._tableBasePaths.get(getTableName(table))), aggregateComputation),
            forCurrentColumnOnly
        );
    }

    private getLastRowOfTable(table: TableGlideType): RootPath {
        let lastRow = this._lastTableRows.get(getTableName(table));
        if (lastRow === undefined) {
            const computation = this.getAggregateComputationForSingleValuePosition(
                { kind: SingleValuePositionKind.Last },
                undefined,
                undefined
            );
            assert(!isBuildResult(computation) && computation.tablePath === undefined && computation.canBeGlobal);
            lastRow = this.extractRow(table, computation.computation, false);
            this._lastTableRows.set(getTableName(table), lastRow);
        }
        return lastRow;
    }

    private buildUserProfileRowPath(): RootPath | BuildResult {
        if (this._userProfileRowPath === undefined) {
            const { userProfileTableInfo } = this._inspector;
            if (userProfileTableInfo === undefined) return BuildResult.Faulty;

            const emailPath = this.getVerifiedEmailAddressPath();

            const tablePath = this._tableBasePaths.get(userProfileTableInfo.tableName);
            if (tablePath === undefined) return BuildResult.Faulty;

            // FIXME: It's possible we add this way after all the data is
            // loaded and everything is computed, but we still initialize it
            // as not dirty, which means it'll return `undefined` as its
            // result.
            const filtered = this.addEntity(
                "filter(userProfiles)",
                new FilterHandler(
                    tablePath,
                    new UserProfileFilterPredicate(emailPath, makePath(userProfileTableInfo.emailColumnName)),
                    undefined,
                    this._fallbackUserProfileRowID
                ),
                false
            );
            this._userProfileRowPath = this.addExtractFirstRowEntity("first(filter(userProfiles))", filtered, true);
        }

        return this._userProfileRowPath;
    }

    private getUserProfileColumn(
        columnName: string,
        withFormat: boolean
    ): (TableAndColumn & PathWithThunksFlag) | BuildResult {
        const paths = withFormat ? this._userProfileFormattedPaths : this._userProfilePaths;

        const path = paths.get(columnName);
        if (path !== undefined) return path;

        const { userProfileTableInfo } = this._inspector;
        if (userProfileTableInfo === undefined) return BuildResult.Faulty;

        const userProfileTable = this._inspector.findTable(userProfileTableInfo.tableName);
        if (userProfileTable === undefined) return BuildResult.Faulty;

        const tacInfo = this.lookupTableAndColumn(userProfileTable, columnName, withFormat);
        if (isBuildResult(tacInfo)) return tacInfo;

        let columnPath: RootPath;
        if (tacInfo.isGlobal) {
            columnPath = tacInfo.rootPath;
        } else {
            const userProfileRowPath = this.buildUserProfileRowPath();
            if (isBuildResult(userProfileRowPath)) return userProfileRowPath;

            const handler = new ComputationHandler(
                new SingleLookupComputation(userProfileRowPath, tacInfo.columnPath, tacInfo.rootPath)
            );

            columnPath = this.addEntity(`${prettyPrintTableAndColumn(tacInfo)}(userProfileRow)`, handler, false);
        }

        const result = { table: tacInfo.table, column: tacInfo.column, rootPath: columnPath, usesThunks: false };
        paths.set(columnName, result);
        return result;
    }

    // The returned `tablePath` is the table path to the table or computation
    // for this column, if it's in the default context.
    //
    // `undefined` means a dependency is missing or something's wrong
    private makePathForSourceColumn(
        contextTable: TableGlideType,
        sc: SourceColumn,
        withFormat: boolean
    ): (TableAndColumn & { tablePath: RootPath | undefined; path: Path; usesThunks: boolean }) | BuildResult {
        const path = getSourceColumnPath(sc);
        if (path.length !== 1) {
            return BuildResult.Faulty;
        }
        if (sc.kind === SourceColumnKind.DefaultContext) {
            const tacInfo = this.lookupTableAndColumn(contextTable, path[0], withFormat);
            if (isBuildResult(tacInfo)) return tacInfo;
            if (tacInfo.isGlobal) {
                // To use non-primitive global columns, the computation needs
                // to be combined with that table column's path that is
                // accessed in the non-primitive.
                return {
                    table: tacInfo.table,
                    column: tacInfo.column,
                    path: tacInfo.rootPath,
                    tablePath: undefined,
                    usesThunks: false,
                };
            } else {
                return {
                    table: tacInfo.table,
                    column: tacInfo.column,
                    path: tacInfo.columnPath,
                    tablePath: tacInfo.rootPath,
                    usesThunks: tacInfo.usesThunks,
                };
            }
        } else if (sc.kind === SourceColumnKind.UserProfile) {
            const columnPath = this.getUserProfileColumn(path[0], withFormat);
            if (isBuildResult(columnPath)) return columnPath;
            return {
                table: columnPath.table,
                column: columnPath.column,
                path: columnPath.rootPath,
                tablePath: undefined,
                usesThunks: columnPath.usesThunks,
            };
        } else {
            assert(sc.kind !== SourceColumnKind.ContainingScreen);
            return assertNever(sc.kind);
        }
    }

    private makePathForConstant(value: BasePrimitiveValue): RootPath {
        let path = this._constantPaths.get(value);
        if (path === undefined) {
            path = this.addEntity(`Constant: ${value}`, new ConstantValueHandler(value), false);
            this._constantPaths.set(value, path);
        }
        return path;
    }

    private makePathForColumnOrValue(
        tac: TableAndColumn,
        spec: ColumnOrValueSpecification<BasePrimitiveValue>,
        withFormat: boolean
    ):
        | (Partial<TableAndColumn> & {
              tablePath: RootPath | undefined;
              path: Path;
              usesThunks: boolean;
              column: TableColumn | undefined;
          })
        | BuildResult {
        if (spec.kind === ColumnOrValueKind.Column) {
            return this.makePathForSourceColumn(tac.table, spec.column, withFormat);
        } else if (spec.kind === ColumnOrValueKind.Constant) {
            const path = this.makePathForConstant(spec.value);
            return { tablePath: undefined, path, usesThunks: false, table: undefined, column: undefined };
        } else if (spec.kind === ColumnOrValueKind.Empty) {
            return {
                path: this.getUndefinedPath(),
                tablePath: undefined,
                usesThunks: false,
                table: undefined,
                column: undefined,
            };
        } else if (spec.kind === ColumnOrValueKind.SpecialValue) {
            switch (spec.specialValue) {
                case SpecialValueKind.VerifiedEmailAddress:
                    return {
                        path: this.getVerifiedEmailAddressPath(),
                        tablePath: undefined,
                        usesThunks: false,
                        table: undefined,
                        column: undefined,
                    };
                case SpecialValueKind.Timestamp:
                    return {
                        path: this.getTimestampPath(),
                        tablePath: undefined,
                        usesThunks: false,
                        table: undefined,
                        column: undefined,
                    };
                case SpecialValueKind.UniqueIdentifier:
                    const basePath = defined(this._tableBasePaths.get(getTableName(tac.table)));
                    const columnName = makeRowID();
                    const handler = new UUIDColumnHandler(basePath, columnName);
                    const tablePath = this.addEntity(`${prettyPrintTableAndColumn(tac)}.uuid`, handler, true);
                    return {
                        tablePath,
                        path: makePath(columnName),
                        usesThunks: false,
                        table: undefined,
                        column: undefined,
                    };
                default:
                    return panic(`Special value ${spec.specialValue} not supported in computed columns`);
            }
        } else {
            return assertNever(spec);
        }
    }

    private makePathsForQueryParameters(
        tac: TableAndColumn,
        params: QueryParametersSpecification
    ): { paramPaths: ReadonlyMap<string, Path>; tablePaths: readonly RootPath[]; usesThunks: boolean } | BuildResult {
        const paramPaths = new Map<string, Path>();
        const tablePaths: RootPath[] = [];
        let usesThunks = false;

        for (const [n, s] of params) {
            if (n === "") continue;

            const paramPath = this.makePathForColumnOrValue(tac, s, false);
            if (isBuildResult(paramPath)) return paramPath;

            if (paramPath.tablePath !== undefined) {
                tablePaths.push(paramPath.tablePath);
            }
            paramPaths.set(n, paramPath.path);
            usesThunks ||= paramPath.usesThunks;
        }

        return { paramPaths, tablePaths, usesThunks };
    }

    private lookupTableAndColumn(
        tableOrTableName: TableGlideType | UniversalTableName,
        columnName: string,
        withFormat: boolean,
        // old-style relation targets are always looked up as the columns
        // directly, not as potential computed columns.
        oldStyleRelation: boolean = false,
        countAsDependency: boolean = true
    ): TableAndColumnInfo | BuildResult {
        const table = isUniversalTableName(tableOrTableName)
            ? this._inspector.findTable(tableOrTableName)
            : tableOrTableName;
        if (table === undefined) return BuildResult.Faulty;
        const tableName = getTableName(table);

        const column = getTableColumn(table, columnName);
        if (column === undefined) return BuildResult.Faulty;

        let tac: TableAndColumn | undefined;
        if (!oldStyleRelation) {
            tac = this._tableAndColumns.get(tableName).get(column);
            if (withFormat && tac !== undefined) {
                const formattedPath = this._formatPaths.get(tac);
                if (formattedPath !== undefined) {
                    return makeTableAndColumnInfo(table, column, formattedPath);
                }
            }

            if (countAsDependency && tac !== undefined && this._currentTableAndColumn !== undefined) {
                this._currentTableAndColumn.columnDependencies.add(tac);
            }
        }

        const columnIsOldStyleRelation = isOldStyleRelation(column) !== undefined;

        if (tac !== undefined) {
            const pathForColumn = this._columnPaths.get(tac);
            if (pathForColumn !== undefined) {
                // We tried building the computed column, but couldn't, or there's
                // an infinite recursion.
                if (isBuildResult(pathForColumn)) return pathForColumn;

                return makeTableAndColumnInfo(table, column, pathForColumn);
            }
        }

        if (!isComputedColumn(column) && isSheetArrayType(column.type) === undefined) {
            if (columnIsOldStyleRelation) {
                if (!oldStyleRelation) return BuildResult.Faulty;
            }

            const tablePath = defined(this._tableBasePaths.get(tableName));
            return {
                table,
                column,
                isGlobal: false,
                rootPath: tablePath,
                columnPath: makeKeyPath(column.name),
                tableColumnPath: amendPath(tablePath, { c: column.name }),
                usesThunks: false,
            };
        }

        if (columnIsOldStyleRelation) return BuildResult.Faulty;

        if (tac === undefined) {
            // We haven't tried building the computed column yet.
            return BuildResult.WaitingOnDependency;
        }

        // This can happen when a column refers directly to itself.
        return BuildResult.Faulty;
    }

    // FIXME: Be smarter here.  Some of these paths can include others as
    // dependencies, in which case we only have to include the former.  We
    // also have to only make at most one combination per set of paths.
    private combineTablePaths(paths: readonly RootPath[], forCurrentColumnOnly: boolean): RootPath {
        assert(paths.every(isTopLevelPath));
        const keys = new Set(paths.map(p => p.rest.key));
        assert(keys.size > 0);
        const uniquePaths = Array.from(keys).map(k => makeRootPath(k));
        const mainKey = paths[0].rest.key;
        assert(mainKey === uniquePaths[0].rest.key);
        if (uniquePaths.length === 1) return uniquePaths[0];

        const entries = this._combinedPaths.get(mainKey);
        let entry = entries.find(([k]) => areEqual(k, keys));
        if (entry === undefined) {
            entry = [
                keys,
                this.addEntity(
                    "combine",
                    new CombineHandler(
                        paths[0],
                        uniquePaths.map(p => ({
                            kind: "pass-through",
                            path: p,
                        }))
                    ),
                    forCurrentColumnOnly
                ),
            ];
            entries.push(entry);
        }

        return entry[1];
    }

    private buildComputedColumn(
        tac: TableAndColumn,
        columnNameOverride: string | undefined
    ): PathForColumn | BuildResult {
        let handler: Handler;
        let isGlobal = false;
        let usesThunks = false;

        const tableName = getTableName(tac.table);
        const columnName = columnNameOverride ?? tac.column.name;

        // `tablePath !== undefined` means uses default context
        const makeComputationHandlerAndSetIsGlobal = (
            computation: Computation,
            tablePaths: RootPath | readonly RootPath[] | undefined
        ): Handler => {
            if (tablePaths === undefined || (isArray(tablePaths) && tablePaths.length === 0)) {
                isGlobal = true;
                return new ComputationHandler(computation);
            } else {
                return new ComputationComputedColumnHandler(
                    this.combineTablePaths(isArray(tablePaths) ? tablePaths : [tablePaths], true),
                    columnName,
                    computation,
                    usesThunks
                );
            }
        };

        const makeAsyncComputationHandlerAndSetIsGlobal = (
            computation: AsyncComputation,
            tablePaths: readonly RootPath[]
        ): Handler => {
            usesThunks = true;
            if (tablePaths.length === 0) {
                isGlobal = true;
                return new AsyncComputationHandler(computation);
            } else {
                return new AsyncComputationComputedColumnHandler(
                    this.combineTablePaths(tablePaths, true),
                    columnName,
                    computation
                );
            }
        };

        const finishWithHandler = (n?: string): PathForColumn => {
            const newPath = this.addEntity(prettyPrintTableAndColumn(tac), defined(handler), true);
            if (isGlobal) {
                assert(n === undefined);
            }
            if (isGlobal) {
                return { isGlobal: true, rootPath: newPath, canBeDeleted: true, usesThunks: false };
            } else {
                const name = n ?? columnName;
                return {
                    isGlobal: false,
                    rootPath: newPath,
                    columnPath: makeKeyPath(name),
                    tableColumnPath: amendPath(newPath, { c: name }),
                    usesThunks,
                };
            }
        };

        const makeTableAggregateComputedColumnHandler = (
            tableOrArrayInfo: TableAndColumnInfo,
            additionalTablePath: RootPath | undefined,
            aggregatedTablePath: RootPath | undefined,
            computation: TableAggregateComputation<unknown>
        ): Handler => {
            let contextTablePath: RootPath;
            if (tableOrArrayInfo.isGlobal) {
                if (additionalTablePath !== undefined) {
                    contextTablePath = additionalTablePath;
                } else {
                    contextTablePath = defined(this._tableBasePaths.get(tableName));
                }
            } else {
                if (additionalTablePath === undefined) {
                    contextTablePath = tableOrArrayInfo.rootPath;
                } else {
                    contextTablePath = this.combineTablePaths([tableOrArrayInfo.rootPath, additionalTablePath], true);
                }
            }

            usesThunks ||= tableOrArrayInfo.usesThunks;

            return new TableAggregateComputedColumnHandler(
                contextTablePath,
                tableOrArrayInfo.isGlobal ? tableOrArrayInfo.rootPath : tableOrArrayInfo.columnPath,
                aggregatedTablePath,
                computation,
                columnName,
                usesThunks
            );
        };

        const makeArrayRollup = (
            arrayInfo: TableAndColumnInfo,
            // See comment below in `makeRollup`
            additionalTablePath: RootPath | undefined,
            computation: TableAggregateComputation<unknown>,
            canBeGlobal: boolean
        ): Handler | BuildResult => {
            const arrayType = arrayInfo.column.type;
            if (arrayType.kind !== "array" || !isPrimitiveType(arrayType.items)) {
                logError("Rollup target is not a primitive array");
                return BuildResult.Faulty;
            }

            if (canBeGlobal && arrayInfo.isGlobal && additionalTablePath === undefined) {
                isGlobal = true;
                return new TableAggregateHandler(arrayInfo.rootPath, computation);
            } else {
                return makeTableAggregateComputedColumnHandler(arrayInfo, additionalTablePath, undefined, computation);
            }
        };

        const makeSingleValueFromArray = (
            arrayInfo: TableAndColumnInfo,
            position: SingleValuePosition
        ): Handler | BuildResult => {
            let additionalTablePath: RootPath | undefined;
            let canBeGlobal = true;

            let computation: TableAggregateComputation<unknown>;
            switch (position.kind) {
                case SingleValuePositionKind.First:
                case SingleValuePositionKind.Last:
                    computation = new ExtractNthItemComputation(
                        undefined,
                        position.kind === SingleValuePositionKind.First ? FirstOrLast.First : FirstOrLast.Last,
                        this.makePathForConstant(0)
                    );
                    break;
                case SingleValuePositionKind.FromStart:
                case SingleValuePositionKind.FromEnd:
                    const positionPaths = this.getPathsForSingleValueOffset(tac.table, position.offset);
                    if (isBuildResult(positionPaths)) return positionPaths;
                    computation = new ExtractNthItemComputation(
                        undefined,
                        position.kind === SingleValuePositionKind.FromStart ? FirstOrLast.First : FirstOrLast.Last,
                        positionPaths.path
                    );
                    if (positionPaths.tablePath !== undefined) {
                        additionalTablePath = positionPaths.tablePath;
                    }
                    break;
                case SingleValuePositionKind.Random:
                    computation = new ExtractRandomItemComputation();
                    canBeGlobal = false;
                    break;
                default:
                    return assertNever(position);
            }

            return makeArrayRollup(arrayInfo, additionalTablePath, computation, canBeGlobal);
        };

        const makeRollup = (
            arraySource: ArraySourceSpecification,
            // If the rollup computation requires additional input from the
            // context table, this is the path to it.  The only case where we
            // currently need it is for the separator in join.  If the
            // relation itself is global, but the separator is in the context
            // table, the result is still non-global.  We must also combine
            // with this table.
            additionalTablePath: RootPath | undefined,
            withFormat: boolean,
            makeComputation: (valuePath: Path | undefined) => TableAggregateComputation<unknown>
        ): Handler | BuildResult => {
            const { tableOrRelationColumn, valueColumn } = arraySource;

            if (valueColumn === undefined) {
                if (typeof tableOrRelationColumn !== "string") {
                    return panic("Rollup over table rows not supported");
                }

                const arrayInfo = this.lookupTableAndColumn(tac.table, tableOrRelationColumn, withFormat);
                if (isBuildResult(arrayInfo)) return arrayInfo;

                return makeArrayRollup(arrayInfo, additionalTablePath, makeComputation(undefined), true);
            }

            let targetTable: TableGlideType | undefined;
            if (typeof tableOrRelationColumn === "string") {
                const relationColumn = getTableColumn(tac.table, tableOrRelationColumn);
                if (relationColumn === undefined) {
                    logError("Rollup target column not found");
                    return BuildResult.Faulty;
                }

                if (!isMultiRelationType(relationColumn.type)) {
                    logError("Rollup target is not a multi-relation");
                    return BuildResult.Faulty;
                }

                targetTable = this._inspector.findTable(relationColumn.type.items);
            } else {
                targetTable = this._inspector.findTable(tableOrRelationColumn);
            }
            if (targetTable === undefined) {
                logError("Rollup target table not found");
                return BuildResult.Faulty;
            }

            const valueInfo = this.lookupTableAndColumn(targetTable, valueColumn, withFormat);
            if (isBuildResult(valueInfo)) return valueInfo;
            usesThunks ||= valueInfo.usesThunks;

            let valuePath: Path;
            let tablePath: RootPath;
            if (valueInfo.isGlobal) {
                valuePath = valueInfo.rootPath;
                tablePath = defined(this._tableBasePaths.get(getTableName(targetTable)));
            } else {
                valuePath = valueInfo.columnPath;
                tablePath = valueInfo.rootPath;
            }

            const computation = makeComputation(valuePath);

            if (typeof tableOrRelationColumn === "string") {
                const tableInfo = this.lookupTableAndColumn(tac.table, tableOrRelationColumn, false);
                if (isBuildResult(tableInfo)) return tableInfo;
                usesThunks ||= tableInfo.usesThunks;

                if (tableInfo.isGlobal && additionalTablePath === undefined) {
                    let aggregatedTablePath: RootPath;
                    if (valueInfo.isGlobal) {
                        aggregatedTablePath = tableInfo.rootPath;
                    } else {
                        aggregatedTablePath = this.addEntity(
                            "combine for rollup",
                            new CombineHandler(tableInfo.rootPath, [
                                {
                                    kind: "pass-through",
                                    path: tableInfo.rootPath,
                                },
                                {
                                    kind: "full-table",
                                    tablePath: valueInfo.rootPath,
                                },
                            ]),
                            true
                        );
                    }
                    isGlobal = true;
                    return new TableAggregateHandler(aggregatedTablePath, computation);
                } else {
                    return makeTableAggregateComputedColumnHandler(
                        tableInfo,
                        additionalTablePath,
                        tablePath,
                        computation
                    );
                }
            } else {
                if (additionalTablePath === undefined) {
                    isGlobal = true;
                    return new TableAggregateHandler(tablePath, computation);
                } else {
                    return new TableAggregateComputedColumnHandler(
                        additionalTablePath,
                        tablePath,
                        tablePath,
                        computation,
                        columnName,
                        usesThunks
                    );
                }
            }
        };

        const makeGuardedForward = (
            forwardee: RootPath,
            relationInfo: TableAndColumnInfo,
            kind: ForwardingKind
        ): PathForColumn => {
            // In this case we're getting a global value, but we're
            // getting it through a relation, and that relation can be
            // empty, in which case the result would also be empty.
            const computation = new GlobalGuardedForwardComputation(
                forwardee,
                relationInfo.isGlobal ? relationInfo.rootPath : relationInfo.columnPath,
                kind
            );
            if (relationInfo.isGlobal) {
                handler = new ComputationHandler(computation);
                isGlobal = true;
            } else {
                handler = new ComputationComputedColumnHandler(
                    relationInfo.rootPath,
                    columnName,
                    computation,
                    usesThunks
                );
            }
            return finishWithHandler();
        };

        const itemColumnNames = isSheetArrayType(tac.column.type);
        // Lookup and single-value columns take their types from the columns
        // they look up, so if those columns have item column names set, the
        // computed columns' types will, too, but we must handle them like
        // regular computed columns.
        if (!isComputedColumn(tac.column) && itemColumnNames !== undefined) {
            const tablePaths: RootPath[] = [];
            let buildResult: BuildResult | undefined;
            const paths = mapFilterUndefined(itemColumnNames, c => {
                const p = this.makePathForSourceColumn(tac.table, makeSourceColumn(c), false);
                if (isBuildResult(p)) {
                    buildResult = p;
                    return undefined;
                }
                if (p.tablePath !== undefined) {
                    tablePaths.push(p.tablePath);
                }
                usesThunks ||= p.usesThunks;
                return p.path;
            });
            if (buildResult !== undefined) return buildResult;
            handler = makeComputationHandlerAndSetIsGlobal(new MakeArrayComputation(paths), tablePaths);
            return finishWithHandler();
        }

        const oldStyleRelation = isOldStyleRelation(tac.column);
        if (oldStyleRelation !== undefined) {
            const targetInfo = this.lookupTableAndColumn(
                oldStyleRelation.tableName,
                oldStyleRelation.columnName,
                false,
                true
            );
            if (isBuildResult(targetInfo)) return targetInfo;

            // Old-style relations can only refer to sheet columns which can't
            // be global.
            if (targetInfo.isGlobal) return BuildResult.Faulty;

            const hostTableColumnPath = amendPath(defined(this._tableBasePaths.get(tableName)), { c: tac.column.name });
            const name = columnNameOverride ?? makeRowID();

            if (oldStyleRelation.isMultiple) {
                handler = new MultiRelationToColumnComputedColumnHandler(
                    hostTableColumnPath,
                    targetInfo.tableColumnPath,
                    name,
                    rowIndexPath,
                    true,
                    targetInfo.column.type.kind === "array"
                );
                usesThunks = true;
            } else {
                handler = new SingleRelationToColumnComputedColumnHandler(
                    hostTableColumnPath,
                    targetInfo.tableColumnPath,
                    name,
                    true,
                    isRelationKeyArray(targetInfo.column)
                );
                usesThunks = true;
            }

            return finishWithHandler(name);
        }

        const spec = decomposeAll(defined(tac.column.formula));
        if (spec === undefined) return BuildResult.Faulty;

        switch (spec.kind) {
            case SyntheticColumnKind.FilterReference: {
                const hostInfo = this.lookupTableAndColumn(tac.table, spec.hostColumn, false);
                if (isBuildResult(hostInfo)) return hostInfo;
                usesThunks ||= hostInfo.usesThunks;
                const { column: hostColumn } = hostInfo;

                if (
                    !isPrimitiveType(hostColumn.type) &&
                    !(hostColumn.type.kind === "array" && isPrimitiveType(hostColumn.type.items))
                ) {
                    return BuildResult.Faulty;
                }

                const targetInfo = this.lookupTableAndColumn(spec.targetTable, spec.targetColumn, false);
                if (isBuildResult(targetInfo)) return targetInfo;
                usesThunks ||= targetInfo.usesThunks;
                const { column: targetColumn } = targetInfo;

                if (
                    !isPrimitiveType(targetColumn.type) &&
                    !(targetColumn.type.kind === "array" && isPrimitiveType(targetColumn.type.items))
                ) {
                    return BuildResult.Faulty;
                }

                const sourceKeyIsArray = isRelationKeyArray(hostInfo.column);
                const targetKeyIsArray = isRelationKeyArray(targetInfo.column);
                if (targetInfo.isGlobal) {
                    const { rootPath: targetPath, table: targetTable } = targetInfo;
                    const targetTablePath = defined(this._tableBasePaths.get(getTableName(targetTable)));
                    if (spec.multiple) {
                        if (hostInfo.isGlobal) {
                            handler = new MultiRelationToGlobalHandler(
                                hostInfo.rootPath,
                                targetTablePath,
                                targetPath,
                                rowIndexPath,
                                sourceKeyIsArray,
                                targetKeyIsArray
                            );
                            isGlobal = true;
                        } else {
                            handler = new MultiRelationToGlobalComputedColumnHandler(
                                hostInfo.tableColumnPath,
                                targetTablePath,
                                targetPath,
                                columnName,
                                rowIndexPath,
                                sourceKeyIsArray,
                                targetKeyIsArray
                            );
                            usesThunks = true;
                        }
                    } else {
                        if (hostInfo.isGlobal) {
                            handler = new TableAggregateHandler(
                                targetTablePath,
                                new SingleRelationComputation(
                                    hostInfo.rootPath,
                                    targetPath,
                                    makePath(rowIndexColumnName),
                                    sourceKeyIsArray,
                                    targetKeyIsArray
                                )
                            );
                            isGlobal = true;
                        } else {
                            handler = new SingleRelationToGlobalComputedColumnHandler(
                                hostInfo.tableColumnPath,
                                targetTablePath,
                                targetPath,
                                columnName,
                                sourceKeyIsArray,
                                targetKeyIsArray
                            );
                            usesThunks = true;
                        }
                    }
                } else {
                    const {
                        rootPath: targetTablePath,
                        columnPath: targetColumnPath,
                        tableColumnPath: targetTableColumnPath,
                    } = targetInfo;

                    if (spec.multiple) {
                        if (hostInfo.isGlobal) {
                            handler = new MultiRelationToColumnHandler(
                                hostInfo.rootPath,
                                targetTableColumnPath,
                                rowIndexPath,
                                sourceKeyIsArray,
                                targetKeyIsArray
                            );
                            isGlobal = true;
                        } else {
                            handler = new MultiRelationToColumnComputedColumnHandler(
                                hostInfo.tableColumnPath,
                                targetTableColumnPath,
                                columnName,
                                rowIndexPath,
                                sourceKeyIsArray,
                                targetKeyIsArray
                            );
                            usesThunks = true;
                        }
                    } else {
                        if (hostInfo.isGlobal) {
                            handler = new TableAggregateHandler(
                                targetTablePath,
                                new SingleRelationComputation(
                                    hostInfo.rootPath,
                                    targetColumnPath,
                                    makePath(rowIndexColumnName),
                                    sourceKeyIsArray,
                                    targetKeyIsArray
                                )
                            );
                            isGlobal = true;
                        } else {
                            handler = new SingleRelationToColumnComputedColumnHandler(
                                hostInfo.tableColumnPath,
                                targetTableColumnPath,
                                columnName,
                                sourceKeyIsArray,
                                targetKeyIsArray
                            );
                            usesThunks = true;
                        }
                    }
                }
                break;
            }
            case SyntheticColumnKind.IfThenElse: {
                const clauses: IfThenElseClause[] = [];
                const tablePaths: RootPath[] = [];
                const makePathForSourceColumn = (sc: SourceColumn): Path | BuildResult => {
                    const maybePath = this.makePathForSourceColumn(tac.table, sc, false);
                    if (isBuildResult(maybePath)) return maybePath;
                    if (maybePath.tablePath !== undefined) {
                        tablePaths.push(maybePath.tablePath);
                    }
                    usesThunks ||= maybePath.usesThunks;
                    return maybePath.path;
                };
                const convertColumnOrValueSpecification = (
                    cov: ColumnOrValueSpecification<string | number>
                ): PathOrGroundValue<ConditionValuePath> | BuildResult => {
                    if (cov.kind === ColumnOrValueKind.Constant) {
                        return { isPath: false, value: cov.value };
                    } else if (cov.kind === ColumnOrValueKind.Empty) {
                        return { isPath: false, value: undefined };
                    } else {
                        const result = this.makePathForColumnOrValue(tac, cov, false);
                        if (isBuildResult(result)) return result;
                        // We only allow primitive values as results of if-then-else
                        if (result.column !== undefined && !isPrimitiveType(result.column.type)) {
                            return BuildResult.Faulty;
                        }
                        if (result.tablePath !== undefined) {
                            tablePaths.push(result.tablePath);
                        }
                        usesThunks ||= result.usesThunks;
                        return { isPath: true, path: { path: result.path, inHostRow: false } };
                    }
                };

                for (const c of spec.clauses) {
                    let condition: Condition<ConditionValuePath>;
                    try {
                        condition = prepareCondition<ConditionValuePath>(c.condition, {
                            makePathForSourceColumn: sc => {
                                const maybePath = makePathForSourceColumn(sc);
                                // eslint-disable-next-line no-throw-literal
                                if (isBuildResult(maybePath)) throw { buildResult: maybePath };
                                return { path: maybePath, inHostRow: false };
                            },
                            getVerifiedEmailAddressPath: () => ({
                                path: this.getVerifiedEmailAddressPath(),
                                inHostRow: false,
                            }),
                            getTimestampPath: () => ({ path: this.getTimestampPath(), inHostRow: false }),
                            getStartOrEndOfTodayPath: startOrEnd => ({
                                path: this.getStartOrEndOfTodayPath(startOrEnd),
                                inHostRow: false,
                            }),
                        });
                    } catch (e: unknown) {
                        if (hasOwnProperty(e, "buildResult") && isBuildResult(e.buildResult)) {
                            return e.buildResult;
                        } else {
                            throw e;
                        }
                    }

                    const then = convertColumnOrValueSpecification(c.thenValue);
                    if (isBuildResult(then)) return then;

                    clauses.push({ condition, then });
                }
                const elseValue = convertColumnOrValueSpecification(spec.elseValue);
                if (isBuildResult(elseValue)) return elseValue;
                handler = makeComputationHandlerAndSetIsGlobal(
                    new IfThenElseComputation(clauses, elseValue),
                    tablePaths
                );
                break;
            }
            case SyntheticColumnKind.Lookup: {
                if (spec.isMultiRelation) {
                    const rollup = makeRollup(
                        {
                            tableOrRelationColumn: spec.tableOrRelationColumn,
                            valueColumn: spec.columnInRelation,
                        },
                        undefined,
                        false,
                        columnPath => {
                            if (isMultiRelationType(tac.column.type)) {
                                return new MultiRelationLookupComputation(columnPath);
                            } else {
                                return new MultiPrimitiveLookupComputation(columnPath);
                            }
                        }
                    );
                    if (isBuildResult(rollup)) return rollup;

                    handler = rollup;
                } else {
                    assert(typeof spec.tableOrRelationColumn === "string");

                    const relationInfo = this.lookupTableAndColumn(tac.table, spec.tableOrRelationColumn, false);
                    if (isBuildResult(relationInfo)) return relationInfo;
                    usesThunks ||= relationInfo.usesThunks;
                    const relationColumn = relationInfo.column;

                    let relationTable: TableGlideType | undefined;
                    if (spec.isMultiRelation) {
                        if (!isMultiRelationType(relationColumn.type)) {
                            logError("Lookup is multi, but relation isn't.");
                            return BuildResult.Faulty;
                        }
                        relationTable = this._inspector.findTable(relationColumn.type.items);
                    } else {
                        if (!isSingleRelationType(relationColumn.type)) {
                            logError("Lookup is single, but relation isn't.");
                            return BuildResult.Faulty;
                        }
                        relationTable = this._inspector.findTable(relationColumn.type);
                    }
                    if (relationTable === undefined) return BuildResult.Faulty;

                    const columnInRelationInfo = this.lookupTableAndColumn(relationTable, spec.columnInRelation, false);
                    if (isBuildResult(columnInRelationInfo)) return columnInRelationInfo;
                    usesThunks ||= columnInRelationInfo.usesThunks;

                    if (spec.isMultiRelation && columnInRelationInfo.column.type.kind === "array") {
                        logError("Cannot lookup array/table through multi-relation");
                        return BuildResult.Faulty;
                    }

                    if (columnInRelationInfo.isGlobal) {
                        return makeGuardedForward(
                            columnInRelationInfo.rootPath,
                            relationInfo,
                            isRelationType(tac.column.type) ? ForwardingKind.LookupRow : ForwardingKind.LookupPrimitive
                        );
                    }

                    if (relationInfo.isGlobal) {
                        handler = new ComputationHandler(
                            new SingleLookupComputation(
                                relationInfo.rootPath,
                                columnInRelationInfo.columnPath,
                                columnInRelationInfo.rootPath
                            )
                        );
                        isGlobal = true;
                    } else {
                        handler = new ComputationComputedColumnHandler(
                            relationInfo.rootPath,
                            columnName,
                            new SingleLookupComputation(
                                relationInfo.columnPath,
                                columnInRelationInfo.columnPath,
                                columnInRelationInfo.rootPath
                            ),
                            usesThunks
                        );
                    }
                }
                break;
            }
            case SyntheticColumnKind.SingleValue: {
                // FIXME: Don't first/last/nth have to combine with the row index column path?

                const src = spec.arraySource;

                // Special case for extracting from a table where we don't
                // need the context.
                if (
                    typeof src.tableOrRelationColumn !== "string" &&
                    spec.position.kind !== SingleValuePositionKind.Random
                ) {
                    const table = this._inspector.findTable(src.tableOrRelationColumn);
                    if (table === undefined) return BuildResult.Faulty;

                    // `undefined` means not handled
                    const getRow = (): { path: RootPath; canBeDeleted: boolean } | BuildResult | undefined => {
                        assert(table !== undefined);
                        if (spec.position.kind === SingleValuePositionKind.First) {
                            return { path: this.getFirstRowOfTable(table), canBeDeleted: false };
                        } else if (spec.position.kind === SingleValuePositionKind.Last) {
                            return { path: this.getLastRowOfTable(table), canBeDeleted: false };
                        } else {
                            const computation = this.getAggregateComputationForSingleValuePosition(
                                spec.position,
                                undefined,
                                tac.table
                            );
                            if (isBuildResult(computation)) return computation;
                            if (computation.tablePath !== undefined) return undefined;
                            assert(computation.canBeGlobal);
                            return { path: this.extractRow(table, computation.computation, true), canBeDeleted: true };
                        }
                    };

                    if (src.valueColumn === undefined) {
                        const row = getRow();
                        if (isBuildResult(row)) return row;
                        if (row !== undefined) {
                            return {
                                isGlobal: true,
                                rootPath: row.path,
                                canBeDeleted: row.canBeDeleted,
                                usesThunks: false,
                            };
                        }
                    } else {
                        const targetInfo = this.lookupTableAndColumn(src.tableOrRelationColumn, src.valueColumn, false);
                        if (isBuildResult(targetInfo)) return targetInfo;
                        usesThunks ||= targetInfo.usesThunks;

                        if (targetInfo.isGlobal) {
                            return {
                                isGlobal: true,
                                rootPath: targetInfo.rootPath,
                                canBeDeleted: false,
                                usesThunks: false,
                            };
                        } else {
                            const row = getRow();
                            if (isBuildResult(row)) return row;
                            if (row !== undefined) {
                                handler = new ComputationHandler(
                                    new SingleLookupComputation(
                                        row.path,
                                        makePath(src.valueColumn),
                                        targetInfo.rootPath
                                    )
                                );
                                isGlobal = true;
                                return finishWithHandler();
                            }
                        }
                    }
                }

                let relationTable: TableGlideType | undefined;
                let relationTablePath: RootPath;
                let valuePath: KeyPath | undefined;
                let valueTablePath: RootPath | undefined;

                const tablePaths: RootPath[] = [];
                let relationPath: Path | undefined;

                let relationInfo: TableAndColumnInfo | undefined;
                if (typeof src.tableOrRelationColumn === "string") {
                    const maybeRelationInfo = this.lookupTableAndColumn(tac.table, src.tableOrRelationColumn, false);
                    if (isBuildResult(maybeRelationInfo)) return maybeRelationInfo;
                    relationInfo = maybeRelationInfo;

                    usesThunks ||= relationInfo.usesThunks;
                    const relationColumn = relationInfo.column;

                    if (relationColumn.type.kind === "array" && isPrimitiveType(relationColumn.type.items)) {
                        // If the input is an array then we do it via a
                        // rollup.
                        const maybeHandler = makeSingleValueFromArray(relationInfo, spec.position);
                        if (isBuildResult(maybeHandler)) return maybeHandler;

                        handler = maybeHandler;
                        break;
                    }

                    if (!isMultiRelationType(relationColumn.type)) return BuildResult.Faulty;

                    relationTable = this._inspector.findTable(relationColumn.type.items);

                    if (relationInfo.isGlobal) {
                        relationPath = relationInfo.rootPath;
                    } else {
                        tablePaths.push(relationInfo.rootPath);
                        relationPath = relationInfo.columnPath;
                    }
                } else {
                    relationTable = this._inspector.findTable(src.tableOrRelationColumn);
                }

                if (relationTable === undefined) return BuildResult.Faulty;

                if (src.valueColumn === undefined) {
                    relationTablePath = defined(this._tableBasePaths.get(getTableName(relationTable)));
                    valuePath = undefined;
                } else {
                    const columnInRelationInfo = this.lookupTableAndColumn(relationTable, src.valueColumn, false);
                    if (isBuildResult(columnInRelationInfo)) return columnInRelationInfo;
                    usesThunks ||= columnInRelationInfo.usesThunks;

                    if (columnInRelationInfo.isGlobal) {
                        if (relationInfo === undefined) {
                            return {
                                isGlobal: true,
                                rootPath: columnInRelationInfo.rootPath,
                                canBeDeleted: false,
                                usesThunks: false,
                            };
                        } else {
                            return makeGuardedForward(
                                columnInRelationInfo.rootPath,
                                relationInfo,
                                ForwardingKind.SingleValue
                            );
                        }
                    }

                    relationTablePath = columnInRelationInfo.rootPath;
                    valuePath = columnInRelationInfo.columnPath;
                    valueTablePath = columnInRelationInfo.rootPath;
                }

                const computation = this.getAggregateComputationForSingleValuePosition(
                    spec.position,
                    valuePath,
                    tac.table
                );
                if (isBuildResult(computation)) return computation;

                if (relationPath === undefined) {
                    assert(typeof src.tableOrRelationColumn !== "string");
                    relationPath = relationTablePath;
                }

                if (computation.tablePath !== undefined) {
                    tablePaths.push(computation.tablePath);
                }

                if (tablePaths.length === 0 && computation.canBeGlobal) {
                    assert(isRootPath(relationPath));
                    let aggregatedTablePath: RootPath;
                    if (valueTablePath === undefined) {
                        aggregatedTablePath = relationPath;
                    } else {
                        aggregatedTablePath = this.addEntity(
                            "combine for global multi-relation",
                            new CombineHandler(relationPath, [
                                {
                                    kind: "pass-through",
                                    path: relationPath,
                                },
                                {
                                    kind: "full-table-from-column",
                                    tablePath: valueTablePath,
                                    columnName: defined(valuePath).key,
                                },
                            ]),
                            true
                        );
                    }
                    handler = new TableAggregateHandler(aggregatedTablePath, computation.computation);
                    isGlobal = true;
                } else {
                    let combined: RootPath;
                    if (tablePaths.length === 0) {
                        combined = defined(this._tableBasePaths.get(tableName));
                    } else {
                        combined = this.combineTablePaths(tablePaths, true);
                    }

                    handler = new TableAggregateComputedColumnHandler(
                        combined,
                        relationPath,
                        relationTablePath,
                        computation.computation,
                        columnName,
                        usesThunks
                    );
                }
                break;
            }
            case SyntheticColumnKind.TextTemplate: {
                let buildResult: BuildResult | undefined;
                const tablePaths: RootPath[] = [];
                const replacementColumns = new Map<string, Path>(
                    mapFilterUndefined(spec.params, ([p, sc]) => {
                        if (sc === undefined) return undefined;
                        const maybePath = this.makePathForColumnOrValue(tac, sc, true);
                        if (isBuildResult(maybePath)) {
                            buildResult = maybePath;
                            return undefined;
                        }
                        if (maybePath.tablePath !== undefined) {
                            tablePaths.push(maybePath.tablePath);
                        }
                        usesThunks ||= maybePath.usesThunks;
                        return [p, maybePath.path];
                    })
                );
                if (buildResult !== undefined) return buildResult;
                let computation: Computation;
                if (spec.template.kind === ColumnOrValueKind.Constant) {
                    const tokens = tokenizeTemplateString(spec.template.value, Array.from(replacementColumns.keys()));
                    const columnTokens: TextTemplateToken<Path>[] = tokens.map(t => {
                        if (t.isText) {
                            return t;
                        } else {
                            return { isText: false, value: defined(replacementColumns.get(t.value)) };
                        }
                    });
                    computation = new StaticTextTemplateComputation(columnTokens);
                } else {
                    const templatePath = this.makePathForColumnOrValue(tac, spec.template, false);
                    if (isBuildResult(templatePath)) return templatePath;
                    if (templatePath.tablePath !== undefined) {
                        tablePaths.push(templatePath.tablePath);
                    }
                    usesThunks ||= templatePath.usesThunks;
                    computation = new DynamicTextTemplateComputation(templatePath.path, replacementColumns);
                }
                handler = makeComputationHandlerAndSetIsGlobal(computation, tablePaths);
                break;
            }
            case SyntheticColumnKind.Math: {
                const result = parseMath(spec.expr, false);
                if (!result.success) return BuildResult.Faulty;
                assert(result.formula.kind === FormulaKind.WithUserEnteredText);
                const formula = (result.formula as WithUserEnteredTextFormula).formula;
                let buildResult: BuildResult | undefined;
                const tablePaths: RootPath[] = [];
                if (hasMathFormulaRandom(formula)) {
                    tablePaths.push(defined(this._tableBasePaths.get(tableName)));
                }
                const variablePaths = new Map<string, [Path, PrimitiveGlideTypeKind]>(
                    mapFilterUndefined(spec.variables, ([n, va]) => {
                        if (va === SpecialValueKind.Timestamp) {
                            return [n, [this.getTimestampPath(), "date-time"]];
                        } else {
                            const p = this.makePathForSourceColumn(tac.table, va, false);

                            if (isBuildResult(p)) {
                                buildResult = p;
                                return undefined;
                            }
                            if (p.tablePath !== undefined) {
                                tablePaths.push(p.tablePath);
                            }
                            usesThunks ||= p.usesThunks;
                            if (!isPrimitiveType(p.column.type)) {
                                logError("Math column input is not a primitive");
                                buildResult = BuildResult.Faulty;
                                return undefined;
                            }
                            return [n, [p.path, p.column.type.kind]];
                        }
                    })
                );
                if (buildResult !== undefined) return buildResult;
                handler = makeComputationHandlerAndSetIsGlobal(new MathComputation(formula, variablePaths), tablePaths);
                break;
            }
            case SyntheticColumnKind.Rollup: {
                const maybeHandler = makeRollup(spec.arraySource, undefined, false, valuePath => {
                    switch (spec.rollupKind) {
                        case RollupKind.AllTrue:
                            return new AllTrueComputation(valuePath);
                        case RollupKind.SomeTrue:
                            return new SomeTrueComputation(valuePath);
                        case RollupKind.Sum:
                            return new SumComputation(valuePath);
                        case RollupKind.CountNonEmpty:
                            return new CountNonEmptyComputation(valuePath);
                        case RollupKind.Average:
                            return new AverageComputation(valuePath);
                        case RollupKind.Minimum:
                            return new MinimumComputation(valuePath);
                        case RollupKind.Maximum:
                            return new MaximumComputation(valuePath);
                        case RollupKind.Earliest:
                            return new EarliestComputation(valuePath);
                        case RollupKind.Latest:
                            return new LatestComputation(valuePath);
                        case RollupKind.Range:
                            return new RangeComputation(valuePath);
                        case RollupKind.CountTrue:
                            return new CountTrueComputation(valuePath);
                        case RollupKind.CountNotTrue:
                            return new CountNotTrueComputation(valuePath);
                        case RollupKind.CountUnique:
                            return new CountUniqueComputation(valuePath);
                        default:
                            return assertNever(spec.rollupKind);
                    }
                });
                if (isBuildResult(maybeHandler)) return maybeHandler;

                handler = maybeHandler;
                break;
            }
            case SyntheticColumnKind.JoinStrings: {
                const separatorPath = this.makePathForColumnOrValue(tac, spec.separator, true);
                if (isBuildResult(separatorPath)) return separatorPath;
                usesThunks ||= separatorPath.usesThunks;
                const maybeHandler = makeRollup(
                    spec.arraySource,
                    separatorPath.tablePath,
                    true,
                    valuePath => new JoinStringsComputation(valuePath, separatorPath.path)
                );
                if (isBuildResult(maybeHandler)) return maybeHandler;

                handler = maybeHandler;
                break;
            }
            case SyntheticColumnKind.SplitString: {
                const tablePaths: RootPath[] = [];
                const inputPath = this.makePathForSourceColumn(tac.table, spec.stringColumn, true);
                if (isBuildResult(inputPath)) return inputPath;
                if (inputPath.tablePath !== undefined) {
                    tablePaths.push(inputPath.tablePath);
                }
                const separatorPath = this.makePathForColumnOrValue(tac, spec.separator, true);
                if (isBuildResult(separatorPath)) return separatorPath;
                if (
                    (inputPath.column !== undefined && !isPrimitiveType(inputPath.column.type)) ||
                    (separatorPath.column !== undefined && !isPrimitiveType(separatorPath.column.type))
                ) {
                    // To be compatible with the old model.  Technically this
                    // should be `BuildResult.Faulty`.
                    return {
                        isGlobal: true,
                        rootPath: this.getEmptyArrayPath(),
                        canBeDeleted: false,
                        usesThunks: false,
                    };
                }
                if (separatorPath.tablePath !== undefined) {
                    tablePaths.push(separatorPath.tablePath);
                }

                usesThunks ||= inputPath.usesThunks || separatorPath.usesThunks;
                handler = makeComputationHandlerAndSetIsGlobal(
                    new SplitStringComputation(inputPath.path, separatorPath.path),
                    tablePaths
                );
                break;
            }
            case SyntheticColumnKind.MakeArray: {
                let buildResult: BuildResult | undefined;
                const tablePaths: RootPath[] = [];
                const paths = mapFilterUndefined(spec.items, sc => {
                    const p = this.makePathForColumnOrValue(tac, sc, false);
                    if (isBuildResult(p)) {
                        buildResult = p;
                        return undefined;
                    }
                    if (p.tablePath !== undefined) {
                        tablePaths.push(p.tablePath);
                    }
                    usesThunks ||= p.usesThunks;
                    return p.path;
                });
                if (buildResult !== undefined) return buildResult;
                handler = makeComputationHandlerAndSetIsGlobal(new MakeArrayComputation(paths), tablePaths);
                break;
            }
            case SyntheticColumnKind.GenerateImage: {
                const seedPath = this.makePathForColumnOrValue(tac, spec.input, false);
                if (isBuildResult(seedPath)) return seedPath;
                usesThunks ||= seedPath.usesThunks;
                handler = makeComputationHandlerAndSetIsGlobal(
                    new GenerateImageComputation(seedPath.path, spec.imageKind === GeneratedImageKind.Mesh),
                    seedPath.tablePath
                );
                break;
            }
            case SyntheticColumnKind.GeoDistance: {
                if (this._appEnvironment === undefined) return BuildResult.Faulty;

                const firstInfo = this.makePathForSourceColumn(tac.table, spec.locationColumn, false);
                if (isBuildResult(firstInfo)) return firstInfo;

                let secondPath: Path;
                let secondTablePath: RootPath | undefined;
                if (spec.otherLocationColumn === "here") {
                    secondPath = this.getCurrentLocationPath();
                } else {
                    const secondInfo = this.makePathForSourceColumn(tac.table, spec.otherLocationColumn, false);
                    if (isBuildResult(secondInfo)) return secondInfo;

                    secondPath = secondInfo.path;
                    secondTablePath = secondInfo.tablePath;
                }

                const computation = new GeoDistanceAsyncComputation(
                    firstInfo.path,
                    secondPath,
                    spec.unit,
                    this._appEnvironment,
                    makeQuotaKeyForFormula(spec)
                );

                const tablePaths: RootPath[] = [];
                if (firstInfo.tablePath !== undefined) {
                    tablePaths.push(firstInfo.tablePath);
                }
                if (secondTablePath !== undefined) {
                    tablePaths.push(secondTablePath);
                }

                handler = makeAsyncComputationHandlerAndSetIsGlobal(computation, tablePaths);
                break;
            }
            case SyntheticColumnKind.UserAPIFetch:
                return missing(tac, "user API fetch");
            case SyntheticColumnKind.ConstructURL: {
                const schemePath = this.makePathForColumnOrValue(tac, spec.scheme, false);
                const hostPath = this.makePathForColumnOrValue(tac, spec.host, false);
                const pathPath = this.makePathForColumnOrValue(tac, spec.path, false);
                if (isBuildResult(schemePath)) return schemePath;
                if (isBuildResult(hostPath)) return hostPath;
                if (isBuildResult(pathPath)) return pathPath;

                const tablePaths = [schemePath.tablePath, hostPath.tablePath, pathPath.tablePath];
                usesThunks ||= schemePath.usesThunks || hostPath.usesThunks || pathPath.usesThunks;

                const paramPaths = this.makePathsForQueryParameters(tac, spec.params);
                if (isBuildResult(paramPaths)) return paramPaths;

                tablePaths.push(...paramPaths.tablePaths);
                usesThunks ||= paramPaths.usesThunks;

                handler = makeComputationHandlerAndSetIsGlobal(
                    new ConstructURLComputation(schemePath.path, hostPath.path, pathPath.path, paramPaths.paramPaths),
                    filterUndefined(tablePaths)
                );
                break;
            }
            case SyntheticColumnKind.YesCode: {
                if (this._appEnvironment === undefined) return BuildResult.Faulty;

                const urlPath = this.makePathForConstant(spec.url);

                const paramPaths = this.makePathsForQueryParameters(tac, spec.params);
                if (isBuildResult(paramPaths)) return paramPaths;

                handler = makeAsyncComputationHandlerAndSetIsGlobal(
                    new YesCodeComputation(urlPath, paramPaths.paramPaths, this._appEnvironment),
                    paramPaths.tablePaths
                );
                break;
            }

            default:
                assertNever(spec);
        }

        return finishWithHandler();
    }

    private buildFormatForPathAndColumn(
        info: PathForColumn,
        displayFormula: Formula,
        entityName: string
    ): PathForColumn | undefined {
        const spec = decomposeFormatFormula(displayFormula);
        if (spec === undefined) return undefined;

        let inputPath: Path;
        let inputTablePath: RootPath | undefined;
        if (info.isGlobal) {
            inputPath = info.rootPath;
        } else {
            inputPath = info.columnPath;
            inputTablePath = info.rootPath;
        }

        let handler: Handler;
        let columnName: string | undefined;
        let usesThunks: boolean;
        if (spec.kind === ValueFormatKind.Number || spec.kind === ValueFormatKind.Duration) {
            const computation =
                spec.kind === ValueFormatKind.Number
                    ? new FormatNumberComputation(inputPath, spec)
                    : new FormatDurationComputation(inputPath, spec);
            if (inputTablePath !== undefined) {
                columnName = makeRowID();
                handler = new ComputationComputedColumnHandler(
                    inputTablePath,
                    columnName,
                    computation,
                    info.usesThunks
                );
                usesThunks = info.usesThunks;
            } else {
                handler = new ComputationHandler(computation);
                usesThunks = false;
            }
        } else if (spec.kind === ValueFormatKind.DateTime) {
            const computation = new FormatDateTimeComputation(inputPath, spec);
            if (inputTablePath !== undefined) {
                columnName = makeRowID();
                handler = new AsyncComputationComputedColumnHandler(inputTablePath, columnName, computation);
                usesThunks = true;
            } else {
                handler = new AsyncComputationHandler(computation);
                usesThunks = false;
            }
        } else {
            return assertNever(spec);
        }

        const path = this.addEntity(entityName, handler, true);

        if (inputTablePath === undefined) {
            return {
                isGlobal: true,
                rootPath: path,
                canBeDeleted: true,
                usesThunks: false,
            };
        } else {
            return {
                isGlobal: false,
                rootPath: path,
                columnPath: makeKeyPath(defined(columnName)),
                tableColumnPath: amendPath(path, { c: defined(columnName) }),
                usesThunks,
            };
        }
    }

    private buildFormat(
        tac: TableAndColumn,
        displayFormula: Formula,
        allowMissingColumn: boolean
    ): PathForColumn | undefined {
        const info = this.lookupTableAndColumn(tac.table, tac.column.name, false, false, false);
        if (isBuildResult(info)) {
            assert(allowMissingColumn && info === BuildResult.Faulty);
            return undefined;
        }

        const entityName = `format(${prettyPrintTableAndColumn(tac)})`;

        return this.buildFormatForPathAndColumn(info, displayFormula, entityName);
    }

    // Returns whether all computed columns have been built and whether there were dependency cycles
    public buildComputedColumns(): ColumnBuildResult {
        if (this._columnBuildResult !== undefined) {
            return this._columnBuildResult;
        }

        this._cachedColumnPaths.clear();

        const tablesAndColumns: TableAndColumn[] = [];

        for (const table of this.getTables()) {
            for (const column of table.columns) {
                const tac: TableAndColumn = { table, column };
                if (
                    isSheetArrayType(column.type) === undefined &&
                    !isComputedColumn(column) &&
                    !isOldStyleRelation(column)
                ) {
                    this.withCurrentTableAndColumn(tac, () => {
                        if (column.displayFormula !== undefined) {
                            this._tableAndColumns.get(getTableName(tac.table)).set(tac.column, tac);
                            const formatPath = this.buildFormat(tac, column.displayFormula, false);
                            if (formatPath !== undefined) {
                                this._formatPaths.set(tac, formatPath);
                            }
                        }
                        return true;
                    });
                    continue;
                }

                tablesAndColumns.push(tac);
            }
        }

        const toDo = new Set(tablesAndColumns);

        let haveMissing = false;
        for (;;) {
            const numToDo = toDo.size;
            if (numToDo === 0) break;
            for (const tac of Array.from(toDo)) {
                this.withCurrentTableAndColumn(tac, () => {
                    this._tableAndColumns.get(getTableName(tac.table)).set(tac.column, tac);
                    const result = this.buildComputedColumn(tac, undefined);
                    let success = false;

                    if (result === BuildResult.WaitingOnDependency) {
                        // logInfo("   ", prettyPrintTableAndColumn(tac));

                        this._columnPaths.set(tac, BuildResult.WaitingOnDependency);
                    } else {
                        if (!isBuildResult(result)) {
                            // We delete first so that `_columnPaths` is the
                            // correct order of computation.
                            this._columnPaths.delete(tac);
                            this._columnPaths.set(tac, result);

                            // logInfo("***", prettyPrintTableAndColumn(tac));

                            const displayFormula = getEffectiveDisplayFormulaForColumn(
                                this._inspector,
                                tac.table,
                                tac.column,
                                new Set()
                            );
                            if (displayFormula !== undefined) {
                                const formatPath = this.buildFormat(tac, displayFormula, false);
                                if (formatPath !== undefined) {
                                    this._formatPaths.set(tac, formatPath);
                                }
                            }

                            success = true;
                        } else if (result === BuildResult.Faulty || result === BuildResult.NotImplemented) {
                            // logInfo("---", prettyPrintTableAndColumn(tac));

                            this._columnPaths.delete(tac);
                            this._columnPaths.set(tac, {
                                isGlobal: true,
                                rootPath: this.getUndefinedPath(),
                                canBeDeleted: false,
                                usesThunks: false,
                            });

                            if (result === BuildResult.NotImplemented) {
                                haveMissing = true;
                            }
                        } else {
                            return assertNever(result);
                        }
                        toDo.delete(tac);
                    }

                    return success;
                });
            }
            if (toDo.size === numToDo) {
                if (numToDo > 0) {
                    haveMissing = true;
                }
                break;
            }
        }

        for (const table of this.getTables()) {
            let tablePath = defined(this._tableBasePaths.get(getTableName(table)));
            const columnPaths: RootPath[] = [];
            for (const [tac, entry] of this._columnPaths.entries()) {
                if (tac.table !== table) continue;
                // This can happen if we have a dependency cycle.
                if (isBuildResult(entry)) continue;
                if (entry.isGlobal) continue;

                columnPaths.push(entry.rootPath);
            }

            for (const [tac, entry] of this._formatPaths.entries()) {
                if (tac.table !== table) continue;

                if (!entry.isGlobal) {
                    columnPaths.push(entry.rootPath);
                }
            }

            tablePath = this.combineTablePaths([tablePath, ...columnPaths], false);
            this._tableFullPaths.set(getTableName(table), tablePath);
        }

        const dependencyGraph = makeGraphFromEdges(this._dependenciesForColumn);
        const cycleNodes = getCycleNodesInGraph(dependencyGraph);
        if (cycleNodes !== undefined) {
            const cycles = getCyclesInGraph(dependencyGraph, cycleNodes);
            assert(cycles.length > 0);
            logError("Computed columns contain cycles");
            for (const cycle of cycles) {
                logError(cycle.map(n => `"${prettyPrintTableAndColumn(n)}"`).join(" -> "));
            }
        }

        this.ns.printSymbolicRepresentation(m => definedMap(m.tableAndColumn, prettyPrintTableAndColumn));

        this._columnBuildResult = { allColumnsBuilt: !haveMissing, dependencyCycles: cycleNodes !== undefined };
        return this._columnBuildResult;
    }

    // `column` must not be included in the table.  It can have the same name
    // as an existing one, but it must not be the same object.
    public buildTemporaryComputedColumn(
        tableName: TableName,
        column: TableColumn,
        columnIsNew: boolean
    ): TemporaryComputedColumn | undefined {
        assert(this._tableBasePaths.has(tableName));
        const table = defined(this._inspector.findTable(tableName));
        assert(!table.columns.includes(column));

        const tac: TableAndColumn = { table, column };

        let pathForValue: PathForColumn | undefined;
        let pathForFormat: PathForColumn | undefined;

        this.withCurrentTableAndColumn(tac, () => {
            if (isComputedColumn(column)) {
                const maybePathForValue = this.buildComputedColumn(tac, makeRowID());
                if (isBuildResult(maybePathForValue)) return false;

                pathForValue = maybePathForValue;
            }

            const displayFormula = getEffectiveDisplayFormulaForColumn(this._inspector, table, column);
            if (displayFormula !== undefined) {
                if (pathForValue === undefined) {
                    pathForFormat = this.buildFormat(tac, displayFormula, columnIsNew);
                } else {
                    pathForFormat = this.buildFormatForPathAndColumn(
                        pathForValue,
                        displayFormula,
                        `format(${prettyPrintTableAndColumn(tac)})`
                    );
                }
            }

            return true;
        });

        if (pathForValue === undefined && pathForFormat === undefined) return undefined;

        return { pathForValue, pathForFormat };
    }

    public deleteTemporaryComputedColumn(t: TemporaryComputedColumn): void {
        const deleteIfNecessary = (p: PathForColumn) => {
            if ((p.isGlobal && p.canBeDeleted) || !p.isGlobal) {
                this.ns.deleteEntity(p.rootPath);
            }
        };

        if (t.pathForFormat !== undefined) {
            deleteIfNecessary(t.pathForFormat);
        }
        if (t.pathForValue !== undefined) {
            deleteIfNecessary(t.pathForValue);
        }
    }

    public setColumnInRow(table: TableGlideType, rowIndex: RowIndex, columnName: string, value: GroundValue): void {
        this.buildComputedColumns();

        const keeper = this.tableKeeperStore.getTableKeeperForTable(getTableName(table));
        keeper.updateRowWithPartialData(rowIndex, { [columnName]: value });
    }

    public getColumnsInComputationOrder(): readonly TableAndColumn[] {
        this.buildComputedColumns();

        return mapFilterUndefined(this._columnPaths, ([k, v]) => (isBuildResult(v) ? undefined : k));
    }

    private getAllColumnPaths(): void {
        for (const v of this._columnPaths.values()) {
            if (isBuildResult(v)) continue;
            this.ns.get(v.rootPath);
        }
        for (const path of this._tableFullPaths.values()) {
            this.ns.get(path);
        }
    }

    public getPathForTable(tableName: TableName): RootPath | undefined {
        this.buildComputedColumns();
        for (const [table, path] of this._tableFullPaths.entries()) {
            if (areTableNamesEqual(tableName, getTableName(table))) {
                return path;
            }
        }
        return undefined;
    }

    public getBasePathForTable(tableName: TableName): RootPath | undefined {
        this.buildComputedColumns();
        return this._tableBasePaths.get(tableName);
    }

    public getDataForTable(tableName: TableName): Table | undefined {
        const path = this.getPathForTable(tableName);
        if (path === undefined) return undefined;

        const tableValue = this.ns.get(path);
        if (isLoadingValue(tableValue)) return undefined;
        return asTable(tableValue);
    }

    public getBaseDataForTable(tableName: TableName): Table | undefined {
        const path = this.getBasePathForTable(tableName);
        if (path === undefined) return undefined;

        const tableValue = this.ns.get(path);
        if (isLoadingValue(tableValue)) return undefined;
        return asTable(tableValue);
    }

    public getColumnPaths(tableName: TableName): ReadonlyMap<string, ValueAndFormatPaths> | undefined {
        const existing = this._cachedColumnPaths.get(tableName);
        if (existing !== undefined) {
            return existing;
        }

        const table = this._inspector.findTable(tableName);
        if (table === undefined) return undefined;

        const baseRootPath = this.getBasePathForTable(tableName);
        if (baseRootPath === undefined) return undefined;

        const result = new Map<string, ValueAndFormatPaths>();

        const entries = this._tableAndColumns.get(tableName);
        if (entries !== undefined) {
            for (const tac of entries.values()) {
                const valueInfo = this._columnPaths.get(tac);
                if (isBuildResult(valueInfo)) continue;

                const formatInfo = this._formatPaths.get(tac);

                result.set(
                    tac.column.name,
                    makeValueAndFormatPaths(tac.column.name, valueInfo, formatInfo, baseRootPath)
                );
            }
        }

        for (const { name } of table.columns) {
            if (result.has(name)) continue;

            result.set(name, [
                [makePath(name), baseRootPath],
                [undefined, undefined],
            ]);
        }

        this._cachedColumnPaths.set(tableName, result);

        return result;
    }

    private getAllTableData(): Map<TableGlideType, Table> {
        this.buildComputedColumns();

        const tableData = new Map<TableGlideType, Table>();
        for (const [tableName, path] of this._tableFullPaths.entries()) {
            const table = this._inspector.findTable(tableName);
            if (table === undefined) continue;
            const tableValue = this.ns.get(path);
            if (isLoadingValue(tableValue)) continue;
            tableData.set(
                table,
                mapFilter(asTable(tableValue), r => r.$isVisible)
            );
        }
        return tableData;
    }

    public getUserProfileRowPath(): RootPath | undefined {
        this.buildComputedColumns();

        const path = this.buildUserProfileRowPath();
        if (isBuildResult(path)) return undefined;

        return path;
    }

    public setData(rowsForTable: Map<TableGlideType, [Row[], UserSpecificRow[]]>, doShuffle: boolean): void {
        this.buildComputedColumns();

        const [time] = withStopwatch(() => {
            for (const tableName of this._tableBasePaths.keys()) {
                const table = this._inspector.findTable(tableName);
                if (table === undefined) continue;

                const keeper = this.tableKeeperStore.getTableKeeperForTable(tableName);
                const userKeeper = this.tableKeeperStore.getUserSpecificTableKeeperForTable(tableName);
                let [rows, userRows] = defined(rowsForTable.get(table));
                if (doShuffle) {
                    // For testing
                    rows = shuffle(rows);
                    userRows = shuffle(userRows);
                }
                for (const row of rows) {
                    keeper.addRow({ ...row });
                    const [numPushes, numProcesses] = this.ns.getAndResetCounters();
                    logInfo(sheetNameForTable(table), numPushes, numProcesses);
                    // for (const [from, map] of pushesFromTo) {
                    //     for (const [to, n] of map) {
                    //         logInfo(from, "->", to, ":", n);
                    //     }
                    // }
                    this._numRowsAfterAdding++;
                }

                if (userKeeper !== undefined) {
                    for (const row of userRows) {
                        userKeeper.addRow(row);
                        const [numPushes, numProcesses] = this.ns.getAndResetCounters();
                        logInfo(sheetNameForTable(table), numPushes, numProcesses);
                    }
                }
            }
        });
        logInfo("adding speed in row/s", this._numRowsAfterAdding / (time / 1000));
    }

    public timeRecompute(writeFile?: (s: string) => void): Map<TableGlideType, Table> {
        this.buildComputedColumns();

        const recompute = (name: string, count: number = 1, before?: () => void) => {
            this.ns.resetTiming();
            const [time] = withStopwatch(() => {
                for (let i = 0; i < count; i++) {
                    before?.();
                    this.getAllColumnPaths();
                }
            });
            logInfo(name, "speed in row/s", (this._numRowsAfterAdding * count) / (time / 1000));
        };

        recompute("after adding", undefined);

        recompute("full recompute", undefined, () => this.ns.setAllDirty());

        if (writeFile !== undefined) {
            recompute("no changes");
            recompute("from scratch", 10, () => this.ns.setAllDirty());

            writeFile(this.ns.makeGraphviz());

            if (printTables) {
                // FIXME: We should really get it via its path, not via the
                // keeper.
                for (const [tableName, path] of this._tableBasePaths.entries()) {
                    const tableValue = this.ns.get(path);
                    if (isLoadingValue(tableValue)) continue;

                    tableForEach(asTable(tableValue), false, r => {
                        const table = this._inspector.findTable(tableName);
                        if (table === undefined) return;

                        logInfo("===", sheetNameForTable(table), r.$rowID);
                        for (const c of table.columns) {
                            const v = getRowColumn(r, c.name);
                            if (v === undefined) continue;
                            logInfo(getTableColumnDisplayName(c), ":", v);
                        }
                        for (const [tac, globalPath] of this._columnPaths.entries()) {
                            if (isBuildResult(globalPath)) continue;
                            if (!globalPath.isGlobal || !areTableNamesEqual(tableName, getTableName(tac.table)))
                                continue;
                            const v = this.ns.get(globalPath.rootPath);
                            logInfo("*", getTableColumnDisplayName(tac.column), ":", v);
                        }
                    });
                }
            }
        }

        return this.getAllTableData();
    }

    public recompute(setAllDirty: boolean, printTiming: boolean, useStrictMode: boolean): RecomputeResult {
        if (useStrictMode) {
            this.ns.setStrictMode(true);
        } else {
            this.ns.setStrictMode(false);
        }

        this.buildComputedColumns();

        if (setAllDirty) {
            this.ns.setAllDirty();
        }

        this.ns.resetTiming(printTiming);
        this.getAllColumnPaths();
        const tableData = this.getAllTableData();

        const stats: RecomputeTiming = new Map();
        if (printTiming) {
            logInfo("*** TIMING ***");

            for (const t of this.ns.getTiming()) {
                logInfo(t);

                const tableAndColumn = t.metadata?.tableAndColumn;

                // NOTE: If a table and column is not in the metadata, store the timing info in a table "unknown" with
                // the column name as the debug name provided when creating the entity
                const tableName = tableAndColumn !== undefined ? getTableName(tableAndColumn.table).name : "unknown";
                const columnName = tableAndColumn !== undefined ? tableAndColumn.column.name : t.name;

                const tableEntry = stats.get(tableName) ?? new Map();
                tableEntry.set(columnName, { time: t.totalTime });
                stats.set(tableName, tableEntry);
            }
        }

        // Reset strict mode
        this.ns.setStrictMode(false);

        return { tableData, stats };
    }
}
